import {
  CUSTOM_FIELDS_BOOLEAN_STRING,
  CUSTOM_FIELDS_TARGET,
  CUSTOM_FIELDS_VALUE_TYPE,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { LoanApplicationTabName } from 'constants/loanApplication';
import { LoanChangesHistoryEntityType } from 'constants/loanChanges';

export const CUSTOM_FIELD_DELETED_LABEL = '[Deleted custom field]';

const CUSTOM_FIELD_PRIMARY_KEY_SEGMENT_COUNT = 3;
const VALUE_PROPERTY_NAME = 'value';

const NOISE_CUSTOM_FIELD_PROPERTY_NAMES = new Set([
  'applicationId',
  'createdByUserId',
  'createdDate',
  'customFieldDefinitionId',
  'targetId',
]);

export const parseCustomFieldEntityPrimaryKey = entityPrimaryKey => {
  if (!_.isString(entityPrimaryKey)) {
    return null;
  }

  const segments = entityPrimaryKey.split('_');

  if (segments.length !== CUSTOM_FIELD_PRIMARY_KEY_SEGMENT_COUNT) {
    return null;
  }

  const [definitionId, applicationId, targetId] = segments;

  return { definitionId, applicationId, targetId };
};

export const isCustomFieldHistoryProperty = propertyName => propertyName === VALUE_PROPERTY_NAME;

export const isCustomFieldHistoryNoiseProperty = propertyName =>
  NOISE_CUSTOM_FIELD_PROPERTY_NAMES.has(propertyName);

const formatDate = value => {
  if (!dayjs(value).isValid()) {
    return value;
  }

  return dayjs(value).format(COMMON_DATE_FORMAT);
};

const formatCurrency = value => {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
};

const formatNumber = value => {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toLocaleString('en-US');
};

const formatPercentage = value => {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return `${numericValue}%`;
};

const formatBoolean = value => {
  if (value === CUSTOM_FIELDS_BOOLEAN_STRING.TRUE) {
    return 'Yes';
  }

  if (value === CUSTOM_FIELDS_BOOLEAN_STRING.FALSE) {
    return 'No';
  }

  return value;
};

const FORMATTERS_BY_VALUE_TYPE = {
  [CUSTOM_FIELDS_VALUE_TYPE.DATE]: formatDate,
  [CUSTOM_FIELDS_VALUE_TYPE.CURRENCY]: formatCurrency,
  [CUSTOM_FIELDS_VALUE_TYPE.NUMBER]: formatNumber,
  [CUSTOM_FIELDS_VALUE_TYPE.PERCENTAGE]: formatPercentage,
  [CUSTOM_FIELDS_VALUE_TYPE.BOOLEAN]: formatBoolean,
};

export const formatCustomFieldValue = (value, valueType) => {
  if (_.isNil(value) || value === '') {
    return value;
  }

  const formatter = FORMATTERS_BY_VALUE_TYPE[valueType];

  return formatter ? formatter(value) : value;
};

const TAB_NAME_BY_CUSTOM_FIELD_TARGET = {
  [CUSTOM_FIELDS_TARGET.LOAN]: LoanApplicationTabName.LOAN,
  [CUSTOM_FIELDS_TARGET.BORROWER]: LoanApplicationTabName.PERSONAL_INFORMATION,
  [CUSTOM_FIELDS_TARGET.PROPERTY]: LoanApplicationTabName.REO,
  [CUSTOM_FIELDS_TARGET.SUBJECT_PROPERTY]: LoanApplicationTabName.PROPERTY_AND_LOAN,
  [CUSTOM_FIELDS_TARGET.INCOME]: LoanApplicationTabName.INCOME_AND_EMPLOYMENT,
  [CUSTOM_FIELDS_TARGET.ASSET]: LoanApplicationTabName.ASSETS,
  [CUSTOM_FIELDS_TARGET.LIABILITY]: LoanApplicationTabName.LIABILITIES,
  [CUSTOM_FIELDS_TARGET.NBE]: LoanApplicationTabName.PERSONAL_INFORMATION,
};

const isSubjectProperty = ({ target, targetId, subjectPropertyId }) =>
  target === CUSTOM_FIELDS_TARGET.PROPERTY &&
  !_.isNil(subjectPropertyId) &&
  targetId === subjectPropertyId;

export const resolveCustomFieldTabName = ({ target, targetId, subjectPropertyId }) => {
  if (isSubjectProperty({ target, targetId, subjectPropertyId })) {
    return LoanApplicationTabName.PROPERTY_AND_LOAN;
  }

  return TAB_NAME_BY_CUSTOM_FIELD_TARGET[target] ?? null;
};

const ALL_BORROWERS_ENTITY_ID = 'all';
const FINANCIAL_INFO_EDIT_SECTION = 'edit';
const CUSTOM_FIELDS_SECTION_HREF = 'custom-fields';
const LOAN_AND_LOCK_SUBTAB_HREF = 'loan-and-lock';
const PROPERTY_INFO_SUBTAB_HREF = 'property-info';
const BORROWER_SUBTAB_HREF = 'borrower';
const REO_SUBTAB_HREF = 'reo';
const LIABILITIES_SUBTAB_HREF = 'liabilities';
const INCOME_SUBTAB_HREF = 'income-and-employment';
const ASSETS_SUBTAB_HREF = 'assets';

const buildFinancialInfoNavigation = (subtab, targetId) => ({
  activeTab: LoanApplicationTabName.FINANCIAL_INFO,
  activeSubtab: subtab,
  entityId: ALL_BORROWERS_ENTITY_ID,
  section: FINANCIAL_INFO_EDIT_SECTION,
  subsection: targetId,
});

const buildSubjectPropertyNavigation = () => ({
  activeTab: LoanApplicationTabName.PROPERTY_AND_LOAN,
  activeSubtab: PROPERTY_INFO_SUBTAB_HREF,
  section: CUSTOM_FIELDS_SECTION_HREF,
});

const buildBorrowerNavigation = targetId => ({
  activeTab: LoanApplicationTabName.BORROWERS,
  activeSubtab: BORROWER_SUBTAB_HREF,
  entityId: targetId,
  section: CUSTOM_FIELDS_SECTION_HREF,
});

const NAVIGATION_BUILDERS_BY_CUSTOM_FIELD_TARGET = {
  [CUSTOM_FIELDS_TARGET.LOAN]: () => ({
    activeTab: LoanApplicationTabName.PROPERTY_AND_LOAN,
    activeSubtab: LOAN_AND_LOCK_SUBTAB_HREF,
    section: CUSTOM_FIELDS_SECTION_HREF,
  }),
  [CUSTOM_FIELDS_TARGET.BORROWER]: buildBorrowerNavigation,
  [CUSTOM_FIELDS_TARGET.PROPERTY]: targetId =>
    buildFinancialInfoNavigation(REO_SUBTAB_HREF, targetId),
  [CUSTOM_FIELDS_TARGET.SUBJECT_PROPERTY]: buildSubjectPropertyNavigation,
  [CUSTOM_FIELDS_TARGET.LIABILITY]: targetId =>
    buildFinancialInfoNavigation(LIABILITIES_SUBTAB_HREF, targetId),
  [CUSTOM_FIELDS_TARGET.INCOME]: targetId =>
    buildFinancialInfoNavigation(INCOME_SUBTAB_HREF, targetId),
  [CUSTOM_FIELDS_TARGET.ASSET]: targetId =>
    buildFinancialInfoNavigation(ASSETS_SUBTAB_HREF, targetId),
  [CUSTOM_FIELDS_TARGET.NBE]: buildBorrowerNavigation,
};

export const resolveCustomFieldNavigation = ({ target, targetId, subjectPropertyId }) => {
  if (isSubjectProperty({ target, targetId, subjectPropertyId })) {
    return buildSubjectPropertyNavigation();
  }

  const builder = NAVIGATION_BUILDERS_BY_CUSTOM_FIELD_TARGET[target];

  return builder ? builder(targetId) : null;
};

export const buildCustomFieldHistoryLabel = ({ definition }) => {
  const definitionName = definition?.name || CUSTOM_FIELD_DELETED_LABEL;

  return `[${LoanChangesHistoryEntityType.CUSTOM_FIELD}] ${definitionName}`;
};
